import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  SECTORS,
  type CreateSegmentInput,
  type Sector,
} from '@ispot/shared'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreate: (input: CreateSegmentInput) => Promise<void>
}

const SECTOR_LABELS: Record<Sector, string> = {
  private: 'Private',
  public: 'Public',
  cross: 'Cross-sector',
}

export function AddSegmentDialog({ open, onOpenChange, onCreate }: Props) {
  const [name, setName] = useState('')
  const [sector, setSector] = useState<Sector>('private')
  const [vertical, setVertical] = useState('')
  const [description, setDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const reset = () => {
    setName('')
    setSector('private')
    setVertical('')
    setDescription('')
    setError(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) {
      setError('Name is required')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      await onCreate({
        name: name.trim(),
        sector,
        vertical: vertical.trim() || null,
        description: description.trim() || null,
        status: 'active',
      })
      reset()
      onOpenChange(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create segment')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset()
        onOpenChange(v)
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add market segment</DialogTitle>
          <DialogDescription>
            Segments represent slices of your market at sector × vertical × niche granularity.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="segment-name">Name</Label>
            <Input
              id="segment-name"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Offshore Renewables"
              required
              maxLength={200}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Sector</Label>
              <div className="flex flex-wrap gap-1.5">
                {SECTORS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setSector(s)}
                    className="rounded-full border px-3 py-1 text-xs font-medium transition-colors"
                    style={{
                      borderColor:
                        sector === s
                          ? 'var(--color-market)'
                          : 'var(--color-border-subtle)',
                      backgroundColor:
                        sector === s ? 'var(--color-market)' : 'transparent',
                      color:
                        sector === s ? 'white' : 'var(--color-text-secondary)',
                    }}
                  >
                    {SECTOR_LABELS[s]}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="segment-vertical">Vertical (optional)</Label>
              <Input
                id="segment-vertical"
                value={vertical}
                onChange={(e) => setVertical(e.target.value)}
                placeholder="e.g. Energy, Fintech"
                maxLength={200}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="segment-description">Description (optional)</Label>
            <Textarea
              id="segment-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Who are the customers in this segment? What makes them distinct?"
              rows={3}
              maxLength={2000}
            />
          </div>

          {error ? (
            <p
              className="rounded border px-3 py-2 text-xs"
              style={{
                borderColor: '#f4c9c5',
                backgroundColor: '#fdf0ee',
                color: '#8b2d20',
              }}
            >
              {error}
            </p>
          ) : null}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || !name.trim()}>
              {submitting ? 'Adding…' : 'Add segment'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
